// 🚀 Testing auto deploy
import React, { useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Menu,
  X,
  Users,
  Star,
  ArrowRight,
  ChevronDown,
  Award,
  Globe,
  Clock,
  Target,
  CheckCircle,
  Play,
  Heart,
  TrendingUp,
  Phone,
  Mail,
  MessageCircle,
  Zap,
} from "lucide-react";
import ContactForm from "./ContactForm";
import { useLanguage } from "../utils/LanguageContext";
import content from "../../src/content/landingpage.json"


const LexoraLanding = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { language, setLanguage } = useLanguage();
  const [showContactForm, setShowContactForm] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(false);
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);
  const [showMoreMobile, setShowMoreMobile] = useState(false);

  const splitHighlightWord = (text, highlightWord) => {
    if (!text || !highlightWord) {
      return { before: text || '', highlight: '', after: '' };
    }

    const index = text.toLowerCase().indexOf(highlightWord.toLowerCase());

    if (index === -1) {
      return { before: text, highlight: '', after: '' };
    }

    const before = text.slice(0, index);
    const highlight = text.slice(index, index + highlightWord.length);
    const after = text.slice(index + highlightWord.length);

    return { before, highlight, after };
  };

  // Close dropdowns when clicking outside

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      const element = document.getElementById(id);

      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: "smooth" });
        }, 100);
      }
    }


  }, [location]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMoreDropdown && !event.target.closest(".more-dropdown")) {
        setShowMoreDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showMoreDropdown]);

  // Scroll-based parallax
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 1000], [0, 200]);
  const y2 = useTransform(scrollY, [0, 1000], [0, -100]);

  const t = content[language];
  // const h = landingpage[language];

  // Animation variants
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: "easeOut" },
  };

  const staggerContainer = {
    animate: {
      transition: {
        staggerChildren: 0.3,
      },
    },
  };

  const { before, highlight, after } = splitHighlightWord(
    t.about.title,
    "Lexora?"
  );

  const WhatOurClientSay =
    language === "es"
      ? "Lo que Dicen Nuestros Clientes"
      : "What Our Clients Say";

  const highlightWord =
    language === "es" ? "Clientes" : "Clients";

  const {
    before: wocsBefore,
    highlight: wocsHighlight,
    after: wocsAfter,
  } = splitHighlightWord(WhatOurClientSay, highlightWord);



  // Enhanced 3D Sound Wave Logo
  const SoundWaveLogo = () => {
    const uniqueId = Math.random().toString(36).substr(2, 9);

    return (
      <div className="w-10 h-10 bg-gradient-to-r from-[#0B1F3B] to-[#0F172A] rounded-lg flex items-center justify-center relative overflow-hidden shadow-lg">
        <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none">
          <rect x="2" y="11" width="2" height="2" rx="1" fill="currentColor" />
          <rect x="5.5" y="9" width="2" height="6" rx="1" fill="currentColor" />
          <rect x="9" y="7" width="2" height="10" rx="1" fill="currentColor" />
          <rect
            x="12.5"
            y="4"
            width="2"
            height="16"
            rx="1"
            fill="currentColor"
          />
          <rect x="16" y="8" width="2" height="8" rx="1" fill="currentColor" />
          <rect
            x="19.5"
            y="10"
            width="2"
            height="4"
            rx="1"
            fill="currentColor"
          />
        </svg>
      </div>
    );
  };

  // Language Toggle Component
  const LanguageToggle = () => (
    <div className="flex items-center space-x-2  bg-[#011f4b] rounded-full p-1 w-[100px] sm:w-[100px]">
      <button
        onClick={() => setLanguage("es")}
        className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${language === "es"
          ? "bg-gradient-to-r from-[#15B77C] to-[#1AA06A] text-white shadow-lg"
          : "text-white hover:text-white"
          }`}
      >
        ES
      </button>
      <button
        onClick={() => setLanguage("en")}
        className={`px-3 py-1 rounded-full text-sm font-medium transition-all ${language === "en"
          ? "bg-gradient-to-r from-[#15B77C] to-[#1AA06A] text-white shadow-lg"
          : "text-white hover:text-white"
          }`}
      >
        EN
      </button>
    </div>
  );

  const HeroSection = () => {
    // Use a key to prevent re-animation on dropdown state changes
    const heroKey = "hero-section";

    return (

      <section id="top" className="relative min-h-screen flex  justify-center items-end  overflow-hidden">
        {/* Background Elements */}

        <img
          src="assests\images\banner-final.jpg"
          className="absolute inset-0 w-full h-full object-cover"
          alt="background"
        />


        <div className="absolute inset-0 "></div>
        <div className="absolute inset-0">
          <motion.div
            style={{ y: y1 }}
            className="absolute  left-10 w-32 h-32 bg-[#15B77C]/20 rounded-full blur-3xl"
          />
          <motion.div
            style={{ y: y2 }}
            className="absolute bottom-20 right-10 w-48 h-48 bg-white/10 rounded-full blur-3xl"
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 sm:py-20 md:py-20 lg:py-20 py-20 pb-0 sm:pb-0 md:pb-0 lg:pb-0 ">
          <div className="grid lg:grid-cols-1 gap-0 sm:gap-0 md:gap-0 lg:gap-12 xl:gap-12 2xl:gap-12 items-center sm:pt-20 md:pt-20 lg:pt-20 pt-20 pb-0 sm:pb-0 sm:pb-0 lg:pb-0">
            {/* Content */}
            <motion.div
              key={`${heroKey}-content`}
              variants={staggerContainer}
              initial="initial"
              animate="animate"
              className="text-center pb-16"
            >
              <motion.h1
                variants={fadeInUp}
                className="leading-tight sm:leading-tight md:leading-snug lg:leading-snug xl:leading-snug 2xl:leading-normal text-2xl sm:text-2xl md:text-3xl lg:text-3xl font-heading font-light text-white leading-heading tracking-heading mb-6"
              >
                <div class="w-8 border-t-8 border-[#3B82F6]/100 mx-auto mb-3"></div>
                <img alt="Professional" className="w-[350px] mx-auto" width="350"
                  height="auto" src="assests/images/Lexora1.svg"></img>
                <span className="block py-10 text-[30px]">{t.hero.title}</span>
                <div class="text-center mx-auto max-w-[450px]">
                  <div class="w-66 border-t border-[#4A7CFF]/30 mx-auto mb-6"></div>
                  <span class="text-[24px] text-white font-heading font-light bg-gradient-to-r from-[#15B77C] to-[#1AA06A] bg-clip-text text-transparent">
                    {t.hero.subtitle}
                  </span>
                </div>



              </motion.h1>

              <motion.p
                variants={fadeInUp}
                className="text-xl text-white mb-8 leading-body font-body max-w-2xl"
              >
                {/* {t.hero.description} */}
              </motion.p>

              <motion.div
                variants={fadeInUp}
                className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-center"
              >
                <a
                  href="/app"
                  className=" bg-[#4A7CFF] min-w-[180px] btn-secondary border-2 border-transparent text-[#fff] px-4 py-2 rounded-3xl font-semibold hover:bg-[#4A7CFF] hover:text-[#fff]  transition-all text-sm flex items-center justify-center font-body"
                >
                  {t.hero.cta2}
                </a>
                <button
                  onClick={() => setShowContactForm(true)}
                  className=" bg-transparent min-w-[190px] btn-secondary border-2 border-[#4A7CFF] text-[#fff] px-4 py-2 rounded-3xl font-semibold hover:bg-[#4A7CFF] hover:text-[#fff]  transition-all text-sm flex items-center justify-center font-body"
                >
                  {t.hero.cta1}
                </button>
              </motion.div>

              <motion.div
                variants={fadeInUp}
                className="flex flex-col sm:flex-row sm:items-center sm:justify-center lg:justify-start space-y-4 sm:space-y-0 sm:space-x-8 mt-12"
              >
                {/* <div className="flex items-center justify-center lg:justify-start space-x-2">
                  <div className="flex">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className="w-5 h-5 text-yellow-400 fill-current"
                      />
                    ))}
                  </div>
                  <span className="text-white/80 font-body">
                    {t.hero.rating}
                  </span>
                </div> */}
                {/* <div className="flex items-center justify-center lg:justify-start space-x-2">
                  <Users className="w-5 h-5 text-[#15B77C] " />
                  <span className="text-white/80 font-body">
                    {t.hero.professionals}
                  </span>
                </div> */}
              </motion.div>
            </motion.div>

            {/* Hero Image */}
            <motion.div
              key="hero-image-section"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="relative"
            >
              {/* <div className="relative text-center sm:text-center md:text-center lg:text-center xl:text-right 2xl:text-right">
                <img
                  src="assests/images/banner-girl.png"
                  alt={
                    language === "es"
                      ? "Profesional española en traje negro representando éxito empresarial y comunicación profesional"
                      : "Spanish professional in black suit representing business success and professional communication"
                  }
                  className="inline-block"
                  loading="lazy"
                />
                <div className="absolute -bottom-0 -right-4 bg-white/95 rounded-lg px-4 py-3 shadow-lg">
                  <div className="flex items-center space-x-3">
                    <Award className="w-5 h-5 text-[#15B77C]" />
                    <div>
                      <div className="text-lg font-bold text-gray-900/80 text-left font-heading">
                        30+
                      </div>
                      <div className="text-xs text-gray-900/80">
                        {t.hero.yearsExp}
                      </div>
                    </div>
                  </div>
                </div>
              </div> */}
            </motion.div>
          </div>
        </div>
      </section>
    );
  };

  // Value Props Section (Why Choose Lexora?)
  const ValuePropsSection = () => (

    <section
      id="about"
      className="bg-white py-10 sm:py-12 md:py-12 lg:py-16 xl:py-16 2xl:py-24"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-10 sm:mb-10 md:mb-10 lg:mb-12 xl:mb-16 2xl:mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-heading-custom font-bold text-[#0F172A] tracking-heading leading-heading mb-8">
            {before}
            <span className="text-[#4A7CFF] border-b-[5px] border-[rgba(74,124,255,0.2)]">{highlight}</span>
            {after}
          </h2>

          <p className="center mx-auto text-xl text-[#64748B] max-w-3xl font-body leading-body">
            {t.about.description}
          </p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-4 gap-8"
        >
          {t.about.valueProps.map((prop, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="min-h-[200px] light-card rounded-2xl p-8 flex flex-col items-center text-center card-hover"
            >
              {/* Icon */}
              <div className="bg-[radial-gradient(circle_at_20%_20%,rgba(74,124,255,0.10)_0%,rgba(74,124,255,0)_35%),linear-gradient(180deg,#0B1F3B_0%,#08172D_100%)] flex justify-center items-center rounded-full w-16 h-16 text-white mb-4">
                {index === 0 && <Target className="w-8 h-8" />}
                {index === 1 && <Users className="w-8 h-8" />}
                {index === 2 && <Zap className="w-8 h-8" />}
                {index === 3 && <TrendingUp className="w-8 h-8" />}
              </div>

              {/* Content */}
              <div>
                <h3 className="text-xl font-semibold text-[#0F172A] mb-2 font-heading-custom tracking-heading">
                  {prop.title}
                </h3>

                <p className="text-[#64748B] font-body leading-body">
                  {prop.description}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );

  // Services Section
  const ServicesSection = () => (
    <section
      id="services"
      className="bg-[radial-gradient(circle_at_85%_15%,rgba(90,140,255,0.12)_0%,rgba(90,140,255,0)_40%),radial-gradient(circle_at_10%_90%,rgba(120,170,255,0.10)_0%,rgba(120,170,255,0)_45%),linear-gradient(180deg,#0D1F3A_0%,#0B2240_40%,#081C36_100%)]"
    >
      <div className="overlay-service bg-[url('../public/assests/images/image01-Photoroom.png')] bg-cover  bg-no-repeat py-10 sm:py-12 md:py-12 lg:py-16 xl:py-16 2xl:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 ">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-8 sm:mb-10 md:mb-10 lg:mb-16 xl:mb-16 2xl:mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-heading-custom font-bold text-white tracking-heading leading-heading mb-8">
              {t.services.title}
            </h2>
            <p className="text-xl text-[#CBD5E1] max-w-4xl mx-auto font-body leading-body">
              {t.services.subtitle}
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            className="flex flex-wrap justify-center gap-8"
          >
            {t.services.offerings.map((service, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="light-card rounded-2xl p-8 card-hover relative light-card w-full sm:w-[calc(50%-1rem)] lg:w-[calc(33.333%-1.5rem)] max-w-sm"
              >
                <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-r from-[#15B77C] to-[#1AA06A] opacity-10 rounded-full blur-2xl"></div>

                <div className="relative z-10">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-[#fff] text-sm font-semibold px-3 py-1 bg-[#4A7CFF] rounded-full font-body">
                      {service.duration}
                    </span>
                    {index < 5 && (
                      <CheckCircle className="w-6 h-6 font-light text-[#0F172A]" />
                    )}
                  </div>

                  <h3 className="text-xl font-semibold mb-4 font-heading tracking-heading">
                    {service.title}
                  </h3>
                  <p className="text-[#64748B] mb-6 font-body leading-body">
                    {service.description}
                  </p>

                  <ul className="space-y-2">
                    {service.features.map((feature, featureIndex) => (
                      <li
                        key={featureIndex}
                        className="flex items-start space-x-3"
                      >
                        <CheckCircle className="w-5 h-5 text-[#0F172A] mt-1 flex-shrink-0" />
                        <span className="text-[#64748B] font-body">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );

  // Testimonials Section
  const TestimonialsSection = () => (
    <section
      id="testimonials"
      className="py-10 sm:py-12 md:py-12 lg:py-16 xl:py-16 2xl:py-24 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-10 sm:mb-10 md:mb-10 lg:mb-12 xl:mb-16 2xl:mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-heading-custom font-bold text-[#333] tracking-heading leading-heading mb-8">
            {wocsBefore}
            <span className="text-[#4A7CFF] border-b-[5px] border-[rgba(74,124,255,0.2)]">{wocsHighlight}</span>
            {wocsAfter}
          </h2>
          <p className="center mx-auto text-xl text-[#64748B] max-w-3xl font-body leading-body">
            {language === "es"
              ? "Descubre cómo hemos ayudado a profesionales como tú a transformar sus carreras"
              : "Discover how we've helped professionals like you transform their careers"}
          </p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {t.testimonials.map((testimonial, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="dark-panel rounded-xl p-8 relative overflow-hidden"
            >
              <div className="absolute top-0 left-0 w-16 h-16 bg-gradient-to-r from-[#15B77C] to-[#1AA06A] opacity-10 rounded-full blur-xl"></div>

              <div className="relative z-10">
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star
                      key={i}
                      className="w-5 h-5 text-[#4A7CFF] fill-current"
                    />
                  ))}
                </div>

                <blockquote className="text-[#CBD5E1] mb-6 italic font-body leading-body min-h-[152px]">
                  "{testimonial.text}"
                </blockquote>

                <div className="border-t border-white pt-4">
                  <div className="font-semibold text-white font-heading">
                    {testimonial.author}
                  </div>
                  <div className="text-[#64748B] text-sm font-body">
                    {testimonial.role}
                  </div>
                  <div className="text-white/60 text-sm font-body">
                    {testimonial.company}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );

  // CTA Section
  const CTASection = () => (
    <section className=" bg-[radial-gradient(circle_at_85%_15%,rgba(90,140,255,0.12)_0%,rgba(90,140,255,0)_40%),radial-gradient(circle_at_10%_90%,rgba(120,170,255,0.10)_0%,rgba(120,170,255,0)_45%),linear-gradient(180deg,#0D1F3A_0%,#0B2240_40%,#081C36_100%)] rounded-none relative">
      <div class="overlay-service bg-[url('../public/assests/images/image01-Photoroom.png')] bg-cover bg-left  bg-no-repeat py-10 sm:py-12 md:py-12 lg:py-16 xl:py-16 2xl:py-24">
        <div className="absolute inset-0 overflow-hidden">
          <motion.div
            style={{ y: y1 }}
            className="absolute top-10 right-10 w-40 h-40 bg-[#15B77C]/20 rounded-full blur-3xl"
          />
          <motion.div
            style={{ y: y2 }}
            className="absolute bottom-10 left-10 w-32 h-32 bg-white/10 rounded-full blur-3xl"
          />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="mb-8 sm:mb-10 md:mb-10 lg:mb-6 xl:mb-6 2xl:mb-6">
                <h2 className="text-4xl md:text-5xl lg:text-5xl font-heading-custom font-bold text-white leading-heading tracking-heading xl:leading-tight lg:leading-tight">
                  <span className="block mb-2">{t.cta.title}</span>
                  <span className="block gradient-text  ">
                    {t.cta.titleAccent}
                  </span>
                </h2>
              </div>
              <p className="text-xl text-[#CBD5E1] mb-8 font-heading-custom leading-body">
                {t.cta.description}
              </p>

              <div className="space-y-4">
                <button
                  onClick={() => setShowContactForm(true)}
                  className="  bg-[#4A7CFF] min-w-[190px] btn-secondary border-2 border-[#4A7CFF] text-[#fff] px-4 py-2 rounded-3xl font-semibold hover:bg-[transparent] hover:text-[#fff]  transition-all text-sm flex items-center justify-center font-body"
                >
                  {t.cta.button}
                </button>


              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.3 }}
              viewport={{ once: true }}
              className="relative flex justify-center lg:justify-end"
            >
              <div className="min-h-[410px] min-w-[280px] sm:min-w-[390px] light-card max-w-sm p-6 bg-gradient-to-br from-[#0a192f] to-[#12223e] rounded-xl shadow-lg text-white font-sans">
  <div className="info-card-wrapper max-w-[250px] mx-auto">
    <p className="text-3xl pb-6 border-b uppercase text-[#0F172A] border-black-300 mb-4">Get In Touch</p>

    <div className="flex items-center mt-2 mb-4">
      <svg
        className="w-5 h-5 text-[#0F172A] mr-2"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        viewBox="0 0 24 24"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M3 8l9 6 9-6M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
        />
      </svg>

      <div>
        <p className="font-bold text-[#0F172A]">hello@lexora.com</p>
        <p className="text-xs text-[#0F172A]">Direct response within 24h</p>
      </div>
    </div>

    <div className="flex items-center mb-4 mt-4">
      <a onClick={() => setShowContactForm(true)} className="inline-flex items-center space-x-2 group cursor-pointer">
        <svg
          className="w-5 h-5 text-[#0F172A] group-hover:text-[#3B82F6] mt-1"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M3 8l9 6 9-6M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>

        <span className="font-body text-[#0F172A] group-hover:text-[#3B82F6]">
          psdingles@gmail.com
        </span>
      </a>
    </div>

    <a href="tel:+34123456789" className="inline-flex items-center space-x-3 group">
      <Phone className="w-4 h-4 group-hover:text-[#3B82F6]" />
      <span className="font-body text-[#0F172A] group-hover:text-[#3B82F6]">
        633209482
      </span>
    </a>

    <button className=" mt-8 flex items-center px-4 py-2 bg-gradient-to-r from-[#0B1F3B] to-[#0F172A] hover:bg-[#374151] rounded text-gray-300 font-semibold space-x-2 transition">
      <svg
        className="w-5 h-5 text-[#3B82F6]"
        fill="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        <path d="M19 0h-14c-2.76 0-5 2.24-5 5v14c0 2.76 2.24 5 5 5h14c2.76 0 5-2.24 5-5v-14c0-2.76-2.24-5-5-5zm-11.5 19h-3v-10h3v10zm-1.5-11.5a1.5 1.5 0 110-3 1.5 1.5 0 010 3zm14 11.5h-3v-5.5c0-1.3-1.5-1.2-1.5 0v5.5h-3v-10h3v1.6c.9-1.7 4.5-1.8 4.5 1.6v7.8z" />
      </svg>

      <span>Follow us on LinkedIn</span>
    </button>
  </div>
</div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );

  return (
    <div className="min-h-screen bg-lexora-dark">
      {/* <Header /> */}
      <HeroSection />
      {/* Dark charcoal divider */}
      <div className="h-0 bg-gradient-to-r from-[#15B77C] to-[#1AA06A]"></div>
      <ValuePropsSection />
      {/* Dark charcoal divider */}
      <div className="h-0 bg-gradient-to-r from-[#15B77C] to-[#1AA06A]"></div>
      <ServicesSection />
      {/* Dark charcoal divider */}
      <div className="h-0 bg-gradient-to-r from-[#15B77C] to-[#1AA06A]"></div>
      <TestimonialsSection />
      <CTASection />

      {/* Contact Form Modal */}
      {showContactForm && (
        <ContactForm
          onClose={() => setShowContactForm(false)}
          language={language}
        />
      )}

      {/* Coming Soon Modal */}
      {showComingSoon && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-8 max-w-md mx-4 text-center">
            <div className="text-4xl mb-4">🚧</div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4 font-heading tracking-heading">
              {language === "es" ? "Próximamente" : "Coming Soon"}
            </h3>
            <p className="text-gray-600 mb-6 font-body leading-body">
              {language === "es"
                ? "Esta sección estará disponible pronto. Mantente al tanto de las actualizaciones."
                : "This section will be available soon. Stay tuned for updates."}
            </p>
            <button
              onClick={() => setShowComingSoon(false)}
              className="bg-gradient-to-r from-[#15B77C] to-[#1AA06A] text-white px-6 py-2 rounded-full font-semibold hover:shadow-lg transition-all font-body"
            >
              {language === "es" ? "Cerrar" : "Close"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default LexoraLanding;
